import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isApiError } from '../../api/client';
import { auth } from '../../api/endpoints';
import type { AdminIdentity } from '../../api/endpoints';

export const meQueryKey = ['auth', 'me'] as const;

/** Current session. `null` means "checked, and not signed in". */
export function useSession() {
  return useQuery<AdminIdentity | null>({
    queryKey: meQueryKey,
    queryFn: async () => {
      try {
        return await auth.me();
      } catch (error) {
        if (isApiError(error) && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      auth.login(input.email, input.password),
    onSuccess: (identity) => {
      queryClient.setQueryData(meQueryKey, identity);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => auth.logout(),
    onSettled: () => {
      queryClient.setQueryData(meQueryKey, null);
      queryClient.clear();
    },
  });
}
